import { Component, OnInit, OnDestroy, AfterViewChecked, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';
import { Incident } from 'src/api/models';
import { FireService } from 'src/service/fire.service';
//import { MqttDatabusService } from 'src/service/mqtt-databus.service';

// ── Public types ──────────────────────────────────────────────────────────────

export type FeedEventType =
  | 'fire_detected'
  | 'zoomed_image'
  | 'coordinates_updated'
  | 'note_added'
  | 'status_change'
  | 'generic'
  | 'incident_escalated';

export interface FeedEvent {
  id: string;
  type: FeedEventType;
  timestamp: Date;
  user: string;
  incidentId: number;
  incidentTitle: string;
  text: string;
  severity: 'critical' | 'warning' | 'info' | 'success';
  /** Optional extra data a handler may attach for template rendering */
  meta?: Record<string, any>;
}

/** Shape of the raw MQTT payload on telemetry/Incident/{id} topics */
export interface IncidentMqttMessage {
  TimeStamp: string;
  Message: string;
  [key: string]: any;
}

/** A handler converts a raw MQTT message into a partial FeedEvent.
 *  Return null to suppress the event entirely. */
type MessageHandler = (
  incidentId: number,
  msg: IncidentMqttMessage,
  cachedTitle: string
) => HandlerResult | null;

/** Extend handler return value with an optional sound file (relative to assets/sounds/) */
interface HandlerResult extends Partial<FeedEvent> {
  sound?: string;
}

@Component({
  selector: 'app-incident-feed',
  templateUrl: './incident-feed.component.html',
  styleUrls: ['./incident-feed.component.css']
})
export class IncidentFeedComponent implements OnInit, OnDestroy, AfterViewChecked {

  private static readonly MAX_FEED = 100;
  /** Topic pattern: telemetry/Incident/{numericId} */
  private static readonly TOPIC_RE = /^telemetry\/Incident\/(\d+)$/i;
  /** Debounce window before polling the cloud for updated incident data (ms) */
  private static readonly POLL_DELAY_MS = 1500;

  @ViewChild('feedEl', { static: false }) feedEl: ElementRef;
  /** Emitted after a successful cloud poll — parent should update the map marker */
  @Output() incidentRefreshed = new EventEmitter<Incident>();

  events: FeedEvent[] = [];

  private mqttSub: Subscription;
  private previousEventCount = 0;
  private shouldScroll = false;
  /** Cache incidentId → title so feed events stay labelled even before first poll */
  private incidentTitleCache = new Map<number, string>();
  /** Active debounce timers keyed by incidentId */
  private pendingPolls = new Map<number, any>();

  // ── Handler registry ────────────────────────────────────────────────────────
  // Keys are lowercase-trimmed Message strings.
  // To add a new message type: add an entry here and (optionally) a template
  // branch in incident-feed.component.html for its FeedEventType.
  private readonly MESSAGE_HANDLERS: Record<string, MessageHandler> = {

    'fire detected': (_id, _msg, _title) => ({
      type: 'fire_detected',
      severity: 'critical',
      text: 'Fire detected',
      sound: 'fire_detected.mp3'
    }),

    'incident updates with zoomed images': (_id, msg, _title) => ({
      type: 'zoomed_image',
      severity: 'warning',
      text: 'Zoomed image captured',
      meta: { imageUrl: msg['ImageUrl'] ?? null }
    }),

    'zoomed image': (_id, msg, _title) => ({
      type: 'zoomed_image',
      severity: 'warning',
      text: 'Zoomed image captured',
      meta: { imageUrl: msg['ImageUrl'] ?? null }
    }),

    'incident coordinates updated': (_id, msg, _title) => ({
      type: 'coordinates_updated',
      severity: 'info',
      text: 'Incident coordinates updated',
      meta: {
        lat: msg['Lat'] ?? msg['Latitude'] ?? null,
        lon: msg['Lon'] ?? msg['Longitude'] ?? null
      }
    }),

    'note added': (_id, msg, _title) => ({
      type: 'note_added',
      severity: 'info',
      text: msg['Note'] ?? msg['NoteText'] ?? 'Note added',
      meta: { author: msg['User'] ?? msg['Author'] ?? null }
    }),

    'incident status': (_id, msg, _title) => ({
      type: 'status_change',
      severity: 'info',
      text: msg.Message,
      sound: 'status_changed.mp3'
    }),

    'incident escalated': (_id, msg, _title) => ({
      type: 'incident_escalated',
      severity: 'critical',
      text: `Incident escalated`,
      sound: 'launching_drone.mp3'
    }),


    '_default': (_id, msg, _title) => ({
      type: 'generic',
      severity: 'info',
      text: msg.Message
    })
  };
  // ────────────────────────────────────────────────────────────────────────────

  constructor(
    private fire: FireService,
    //private mqtt: MqttDatabusService
  ) {}

  ngOnInit(): void {
    // this.mqttSub = this.mqtt.messages$.subscribe(msg => {
    //   const match = IncidentFeedComponent.TOPIC_RE.exec(msg.fullTopic);
    //   if (!match) { return; }

    //   const incidentId = parseInt(match[1], 10);
    //   let payload: IncidentMqttMessage;
    //   try {
    //     payload = typeof msg.payload === 'string' ? JSON.parse(msg.payload) : msg.payload;
    //   } catch {
    //     return; // malformed JSON — skip
    //   }

    //   this.handleMqttMessage(incidentId, payload);
    //   this.schedulePoll(incidentId);
    // });
  }

  ngOnDestroy(): void {
    this.mqttSub?.unsubscribe();
    this.pendingPolls.forEach(t => clearTimeout(t));
    this.pendingPolls.clear();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll && this.feedEl?.nativeElement) {
      this.feedEl.nativeElement.scrollTop = this.feedEl.nativeElement.scrollHeight;
      this.shouldScroll = false;
    }
  }

  // ── Private ──────────────────────────────────────────────────────────────────

  private handleMqttMessage(incidentId: number, msg: IncidentMqttMessage): void {
    const cachedTitle = this.incidentTitleCache.get(incidentId) ?? `INC-${incidentId}`;
    const key = (msg.Message ?? '').toLowerCase().trim();
    // Exact match first, then prefix match, then default
    const handler =
      this.MESSAGE_HANDLERS[key] ??
      Object.entries(this.MESSAGE_HANDLERS).find(([k]) => k !== '_default' && key.startsWith(k))?.[1] ??
      this.MESSAGE_HANDLERS['_default'];
    const partial = handler(incidentId, msg, cachedTitle);
    if (!partial) { return; }

    if (partial.sound) { this.playSound(partial.sound); }

    const ts = msg.TimeStamp ? new Date(msg.TimeStamp) : new Date();

    this.push({
      id: `${incidentId}-${key}-${ts.getTime()}`,
      type: partial.type ?? 'generic',
      timestamp: ts,
      user: msg['User'] ?? msg['Author'] ?? '',
      incidentId,
      incidentTitle: cachedTitle,
      text: partial.text ?? msg.Message,
      severity: partial.severity ?? 'info',
      meta: partial.meta
    });
  }

  private playSound(filename: string): void {
    try {
      const audio = new Audio(`assets/sounds/${filename}`);
      audio.play().catch(err => console.warn('[IncidentFeed] Sound playback blocked:', err));
    } catch (err) {
      console.warn('[IncidentFeed] Sound error:', err);
    }
  }

  private schedulePoll(incidentId: number): void {
    // Cancel any in-flight timer for this incident and restart the debounce window
    if (this.pendingPolls.has(incidentId)) {
      clearTimeout(this.pendingPolls.get(incidentId));
    }
    const timer = setTimeout(() => {
      this.pendingPolls.delete(incidentId);
      this.fire.getIncident(incidentId, (incident: Incident) => {
        if (!incident) { return; }
        // Update title cache so subsequent events use the real title
        const title = incident.incidentTitle ?? incident.site?.name ?? `INC-${incidentId}`;
        this.incidentTitleCache.set(incidentId, title);
        // Notify parent for map marker update
        this.incidentRefreshed.emit(incident);
      });
    }, IncidentFeedComponent.POLL_DELAY_MS);
    this.pendingPolls.set(incidentId, timer);
  }

  private push(event: FeedEvent): void {
    this.events.push(event);
    if (this.events.length > IncidentFeedComponent.MAX_FEED) {
      this.events.shift(); // Remove oldest from beginning
    }
    // Trigger scroll on next view check if new events were added
    if (this.events.length !== this.previousEventCount) {
      this.shouldScroll = true;
      this.previousEventCount = this.events.length;
    }
  }

  formatTime(date: Date): string {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }
}
